import { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const QUAL_STATUSES = ['I/P', 'Needs Review', 'Needs Revision', 'Approved', 'Backburner'];
const LIVE_STATUSES = ['Scripted', 'Filming', 'Filmed', 'Editing', 'Qual', 'Approved', 'Incomplete', 'Posted'];
const AI_STATUSES   = ['Scripted', 'Editing', 'Qual', 'Approved', 'Posted'];

const QUAL_COLORS = {
  'I/P':            'var(--s-ip)',
  'Needs Review':   'var(--s-nr)',
  'Needs Revision': 'var(--s-revision)',
  'Approved':       'var(--s-approved)',
  'Backburner':     'var(--s-backburner)',
};
const LIVE_COLORS = {
  'Scripted':   'var(--s-scripted)',
  'Filming':    'var(--s-filming)',
  'Filmed':     'var(--s-filmed)',
  'Editing':    'var(--s-editing)',
  'Qual':       'var(--s-qual)',
  'Approved':   'var(--s-approved)',
  'Incomplete': 'var(--s-incomplete)',
  'Posted':     'var(--s-posted)',
};
const AI_COLORS = {
  'Scripted': 'var(--s-scripted)',
  'Editing':  'var(--s-editing)',
  'Qual':     'var(--s-qual)',
  'Approved': 'var(--s-approved)',
  'Posted':   'var(--s-posted)',
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function thisMonth(dateStr) {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const n = new Date();
  return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
}

function inDateRange(dateStr, start, end) {
  if (!dateStr) return false;
  const d = new Date(dateStr).getTime();
  if (start && d < new Date(start).getTime()) return false;
  if (end   && d > new Date(end + 'T23:59:59').getTime()) return false;
  return true;
}

function buildCounts(list, key, statuses) {
  const counts = Object.fromEntries(statuses.map(s => [s, 0]));
  list.forEach(item => {
    const v = item[key];
    if (counts[v] !== undefined) counts[v]++;
  });
  return counts;
}

function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(esc).join(','), ...rows.map(r => headers.map(k => esc(r[k])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Bar chart ─────────────────────────────────────────────────────────────────

function BarChart({ statuses, counts, colors }) {
  const max = Math.max(...Object.values(counts), 1);
  return (
    <div>
      {statuses.map(status => {
        const count = counts[status] || 0;
        const pct   = Math.round((count / max) * 100);
        return (
          <div key={status} className="rpt-bar-row">
            <div className="rpt-bar-label">{status}</div>
            <div className="rpt-bar-track">
              <div className="rpt-bar-fill" style={{ width: `${pct}%`, background: colors[status] || 'var(--violet)' }} />
            </div>
            <div className="rpt-bar-count">{count}</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Summary card ──────────────────────────────────────────────────────────────

function SummaryCard({ num, label, sub }) {
  return (
    <div className="rpt-card">
      <div className="rpt-card-num">{num}</div>
      <div className="rpt-card-label">{label}</div>
      {sub != null && <div style={{ fontSize: 11, color: 'var(--tx-muted)', marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

// ── Collapsible floating card ─────────────────────────────────────────────────

function CollapsibleCard({ id, title, subtitle, defaultOpen = true, children, onPrint, onExport }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rpt-fcrd" id={id}>
      <div className="rpt-fcrd-header" onClick={() => setOpen(o => !o)}>
        <div>
          <div className="rpt-fcrd-title">{title}</div>
          {subtitle && <div className="rpt-fcrd-sub">{subtitle}</div>}
        </div>
        <div className="rpt-fcrd-controls">
          {onExport && (
            <button className="rpt-fcrd-btn" title="Export as CSV" onClick={e => { e.stopPropagation(); onExport(); }}>
              ↓ CSV
            </button>
          )}
          {onPrint && (
            <button className="rpt-fcrd-btn" title="Print this section" onClick={e => { e.stopPropagation(); onPrint(); }}>
              ⎙ Print
            </button>
          )}
          <span className="rpt-fcrd-chevron">{open ? '▲' : '▼'}</span>
        </div>
      </div>
      {open && <div className="rpt-fcrd-body">{children}</div>}
    </div>
  );
}

// ── Print helpers ─────────────────────────────────────────────────────────────

function printSection(elementId, title) {
  const el = document.getElementById(elementId);
  if (!el) return;
  const clone = el.cloneNode(true);
  clone.querySelectorAll('.rpt-fcrd-btn').forEach(btn => btn.remove());
  clone.querySelectorAll('.rpt-fcrd-body').forEach(b => { b.style.display = 'block'; });
  const win = window.open('', '_blank', 'width=900,height=700');
  win.document.write(`<!DOCTYPE html><html><head>
    <title>${title}</title>
    <style>
      body { font-family: system-ui, sans-serif; padding: 24px; color: #111; background: #fff; font-size: 13px; }
      .rpt-fcrd-header { display: flex; justify-content: space-between; margin-bottom: 16px; border-bottom: 1px solid #ddd; padding-bottom: 12px; }
      .rpt-fcrd-title  { font-size: 15px; font-weight: 700; }
      .rpt-fcrd-sub    { font-size: 12px; color: #555; margin-top: 3px; }
      .rpt-bar-row     { display: grid; grid-template-columns: 140px 1fr 36px; align-items: center; gap: 10px; padding: 4px 0; }
      .rpt-bar-label   { font-size: 11px; text-align: right; }
      .rpt-bar-track   { height: 8px; background: #eee; border-radius: 4px; overflow: hidden; }
      .rpt-bar-fill    { height: 100%; border-radius: 4px; }
      .rpt-bar-count   { font-size: 11px; text-align: right; }
      table            { width: 100%; border-collapse: collapse; }
      th               { padding: 7px 10px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: .05em; background: #f5f5f5; border-bottom: 1px solid #ddd; color: #333; }
      td               { padding: 8px 10px; font-size: 12px; border-bottom: 1px solid #eee; color: #111; }
      tr:last-child td { border-bottom: none; }
    </style>
  </head><body>${clone.outerHTML}</body></html>`);
  win.document.close();
  win.onload = () => { win.print(); win.close(); };
}

// ── Sort helper ───────────────────────────────────────────────────────────────

function SortTh({ label, col, sort, setSort, style }) {
  const active = sort.col === col;
  return (
    <th
      className="rpt-th"
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
      onClick={() => setSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }))}
    >
      {label}
      <span style={{ marginLeft: 4, fontSize: 9, opacity: active ? 1 : 0.2, color: active ? 'var(--violet)' : 'inherit' }}>
        {active && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </th>
  );
}

function applySortRows(rows, sort) {
  if (!sort.col) return rows;
  return [...rows].sort((a, b) => {
    const av = a[sort.col] ?? '';
    const bv = b[sort.col] ?? '';
    const cmp = typeof av === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv));
    return sort.dir === 'asc' ? cmp : -cmp;
  });
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function Reports() {
  const { hasRole, user } = useAuth();
  const canPrint       = hasRole('Admin', 'Manager', 'Power User');
  const canSeeBilling  = hasRole('Admin', 'Manager', 'Power User');
  const isWriterOnly   = hasRole('Writer') && !hasRole('Admin', 'Manager', 'Power User');

  const [scripts,     setScripts]     = useState([]);
  const [liveAll,     setLiveAll]     = useState([]);
  const [aiAll,       setAiAll]       = useState([]);
  const [clients,     setClients]     = useState([]);
  const [editorStats, setEditorStats] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);

  // Filters
  const [clientFilter,      setClientFilter]      = useState('all');
  const [dateStart,         setDateStart]         = useState('');
  const [dateEnd,           setDateEnd]           = useState('');
  const [selectedWriterId,  setSelectedWriterId]  = useState('');

  // Table sort states
  const [clientSort,  setClientSort]  = useState({ col: 'scriptsCount',      dir: 'desc' });
  const [writerSort,  setWriterSort]  = useState({ col: 'total',             dir: 'desc' });
  const [billingSort, setBillingSort] = useState({ col: 'dateEntered',       dir: 'desc' });
  const [editorSort,  setEditorSort]  = useState({ col: 'avgTurnaroundDays', dir: 'asc'  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      // includeInProduction: Reports COUNTS work, so it must keep scripts whose
      // video card has started shooting — the Scripts index hides those behind
      // its Filming pill, but hiding them here would deflate every writer and
      // client total. Mirrors the excludePosted=false intent on the rows below.
      api.get('/api/scripts?includeInProduction=true'),
      api.get('/api/live-production?excludePosted=false'),
      api.get('/api/ai-production?excludePosted=false'),
      api.get('/api/clients'),
      api.get('/api/live-production/editor-stats'),
    ])
      .then(([s, lv, ai, cl, es]) => {
        setScripts(s.data);
        setLiveAll(lv.data);
        setAiAll(ai.data);
        setClients(cl.data);
        setEditorStats(es.data);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setError('Failed to load report data.');
        setLoading(false);
      });
  }, []);

  function setPreset(days) {
    const end   = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setDateStart(start.toISOString().slice(0, 10));
    setDateEnd(end.toISOString().slice(0, 10));
  }

  function setThisMonth() {
    const now   = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    setDateStart(start.toISOString().slice(0, 10));
    setDateEnd(now.toISOString().slice(0, 10));
  }

  function clearDates() {
    setDateStart('');
    setDateEnd('');
  }

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredScripts = useMemo(() => {
    let r = clientFilter === 'all' ? scripts : scripts.filter(s => s.clientId === clientFilter);
    if (dateStart || dateEnd) r = r.filter(s => inDateRange(s.dateEntered, dateStart, dateEnd));
    return r;
  }, [scripts, clientFilter, dateStart, dateEnd]);

  const filteredLive = useMemo(() =>
    clientFilter === 'all' ? liveAll : liveAll.filter(v => v.clientId === clientFilter),
    [liveAll, clientFilter]);

  const filteredAi = useMemo(() =>
    clientFilter === 'all' ? aiAll : aiAll.filter(v => v.clientId === clientFilter),
    [aiAll, clientFilter]);

  // ── Summary counts ────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const totalScripts    = filteredScripts.length;
    const approvedScripts = filteredScripts.filter(s => s.qualStatus === 'Approved').length;
    const liveVideos      = filteredLive.length;
    const aiVideos        = filteredAi.length;
    const livePosted      = filteredLive.filter(v => v.status === 'Posted').length;
    const aiPosted        = filteredAi.filter(v  => v.status === 'Posted').length;
    const scriptsThisMonth = filteredScripts.filter(
      s => thisMonth(s.dateEntered) || thisMonth(s.approvedAt)
    ).length;
    return { totalScripts, approvedScripts, liveVideos, aiVideos, livePosted, aiPosted, scriptsThisMonth };
  }, [filteredScripts, filteredLive, filteredAi]);

  // ── Bar chart counts ──────────────────────────────────────────────────────
  const qualCounts = useMemo(() => buildCounts(filteredScripts, 'qualStatus', QUAL_STATUSES), [filteredScripts]);
  const liveCounts = useMemo(() => buildCounts(filteredLive,    'status',     LIVE_STATUSES), [filteredLive]);
  const aiCounts   = useMemo(() => buildCounts(filteredAi,      'status',     AI_STATUSES),   [filteredAi]);

  // ── Output by Client rows ─────────────────────────────────────────────────
  const clientRows = useMemo(() => {
    const activeClients = clientFilter === 'all'
      ? clients.filter(c => c.isActive)
      : clients.filter(c => c.isActive && c.id === clientFilter);
    const rows = activeClients.map(client => {
      const cScripts = scripts.filter(s => s.clientId === client.id);
      const cLive    = liveAll.filter(v => v.clientId === client.id);
      const cAi      = aiAll.filter(v  => v.clientId === client.id);
      return {
        clientName:    client.name,
        initials:      client.initials,
        clientId:      client.id,
        scriptsCount:  cScripts.length,
        approvedCount: cScripts.filter(s => s.qualStatus === 'Approved').length,
        liveVideos:    cLive.length,
        aiVideos:      cAi.length,
        livePosted:    cLive.filter(v => v.status === 'Posted').length,
        aiPosted:      cAi.filter(v  => v.status === 'Posted').length,
      };
    });
    return applySortRows(rows, clientSort);
  }, [clients, scripts, liveAll, aiAll, clientFilter, clientSort]);

  // ── Scripts by Writer rows ────────────────────────────────────────────────
  const writerRows = useMemo(() => {
    const map = {};
    filteredScripts.forEach(s => {
      const id   = s.writerId;
      const name = s.writer?.name || 'Unknown';
      if (!map[id]) map[id] = { writerName: name, total: 0, approved: 0 };
      map[id].total++;
      if (s.qualStatus === 'Approved') map[id].approved++;
    });
    const rows = Object.values(map).map(r => ({ ...r, approvalPct: r.total > 0 ? Math.round((r.approved / r.total) * 100) : 0 }));
    return applySortRows(rows, writerSort);
  }, [filteredScripts, writerSort]);

  // ── Billing report rows ───────────────────────────────────────────────────
  // Format labeling: 'Recipe' rows are Cooking Phase 1. 'TH' rows that are
  // linked to a Phase 2 live_production (isCookingPhase2 from API) get
  // relabeled as 'TH (Phase 2 — Cooking)' so the writer-paid-per-script
  // billing view doesn't accidentally undercount the cooking workflow.
  const formatFor = (s) => {
    if (s.isCookingPhase2) return 'TH (Phase 2 — Cooking)';
    return s.format || '—';
  };
  const billingRows = useMemo(() => {
    const base = canSeeBilling ? filteredScripts : (isWriterOnly ? filteredScripts.filter(s => s.writerId === user?.id) : []);
    const rows = base.map(s => ({
      writerName:  s.writer?.name || '—',
      clientName:  s.client?.name || '—',
      scriptName:  s.scriptName,
      format:      formatFor(s),
      status:      s.qualStatus,
      dateEntered: s.dateEntered ? s.dateEntered.slice(0, 10) : '—',
    }));
    return applySortRows(rows, billingSort);
  }, [filteredScripts, billingSort, canSeeBilling, isWriterOnly, user]);

  // ── Writer report data ────────────────────────────────────────────────────
  const writerMembers = useMemo(() => {
    const map = {};
    scripts.forEach(s => {
      if (s.writerId && s.writer?.name) map[s.writerId] = s.writer.name;
    });
    return Object.entries(map)
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [scripts]);

  const writerReportId = isWriterOnly ? user?.id : selectedWriterId;

  const writerReportScripts = useMemo(() =>
    writerReportId ? scripts.filter(s => s.writerId === writerReportId) : [],
    [scripts, writerReportId]);

  const writerReportName = useMemo(() => {
    if (!writerReportId) return '';
    return scripts.find(s => s.writerId === writerReportId)?.writer?.name || '';
  }, [scripts, writerReportId]);

  const writerStats = useMemo(() => ({
    total:      writerReportScripts.length,
    approved:   writerReportScripts.filter(s => s.qualStatus === 'Approved').length,
    inQual:     writerReportScripts.filter(s => ['I/P', 'Needs Review', 'Needs Revision'].includes(s.qualStatus)).length,
    backburner: writerReportScripts.filter(s => s.qualStatus === 'Backburner').length,
  }), [writerReportScripts]);

  // ── Subtitle helpers ──────────────────────────────────────────────────────
  const selectedClientName = useMemo(() =>
    clientFilter === 'all' ? null : clients.find(c => c.id === clientFilter)?.name || null,
    [clientFilter, clients]);

  const dateLabel = (dateStart || dateEnd)
    ? `${dateStart || '…'} → ${dateEnd || '…'}`
    : null;

  // ── CSV export helpers ────────────────────────────────────────────────────
  function exportClientRows() {
    downloadCSV(clientRows.map(r => ({
      Client: r.clientName, Scripts: r.scriptsCount, Approved: r.approvedCount,
      'Live Videos': r.liveVideos, 'AI Videos': r.aiVideos,
      'Live Posted': r.livePosted, 'AI Posted': r.aiPosted,
    })), 'output-by-project.csv');
  }

  function exportWriterRows() {
    downloadCSV(writerRows.map(r => ({
      Writer: r.writerName, 'Scripts Written': r.total, Approved: r.approved, 'Approval %': `${r.approvalPct}%`,
    })), 'scripts-by-writer.csv');
  }

  function exportBillingRows() {
    downloadCSV(billingRows.map(r => ({
      Writer: r.writerName, Client: r.clientName, Script: r.scriptName, Format: r.format, Status: r.status, Date: r.dateEntered,
    })), 'scripts-written-billing.csv');
  }

  const sortedEditorStats = useMemo(() => applySortRows(editorStats, editorSort), [editorStats, editorSort]);

  function exportEditorStats() {
    downloadCSV(sortedEditorStats.map(r => ({
      Editor: r.name,
      'Avg Turnaround (days)': r.avgTurnaroundDays.toFixed(1),
      'Revision Rate %': `${r.revisionRate}%`,
      'Current Queue': r.queueDepth,
    })), 'editor-performance.csv');
  }

  function exportWriterDetail() {
    downloadCSV(writerReportScripts.map(s => ({
      Script: s.scriptName,
      Client: s.client?.name || '—',
      Status: s.qualStatus,
      Date:   s.dateEntered ? s.dateEntered.slice(0, 10) : '—',
    })), `writer-report-${writerReportName.replace(/\s+/g, '-').toLowerCase() || 'unknown'}.csv`);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="main">
        <div className="topbar"><div><div className="page-title">Reports</div></div></div>
        <div className="body" style={{ color: 'var(--tx-muted)', fontSize: 13 }}>Loading report data…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="main">
        <div className="topbar"><div><div className="page-title">Reports</div></div></div>
        <div className="body" style={{ color: 'var(--s-incomplete-t)', fontSize: 13 }}>{error}</div>
      </div>
    );
  }

  return (
    <div className="main">
      <div className="topbar">
        <div>
          <div className="page-title">Reports</div>
          <div className="page-sub">Analytics &amp; output summary</div>
        </div>
        <div className="topbar-actions">
          {canPrint && (
            <button className="rpt-print-btn" onClick={() => window.print()}>
              ⎙ Print All
            </button>
          )}
        </div>
      </div>

      <div className="body" style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>

        {/* ── Filter bar ── */}
        <div className="rpt-filter-bar" style={{ flexWrap: 'wrap', gap: 10 }}>
          <span className="rpt-filter-label">Project:</span>
          <select
            className="filter-input"
            value={clientFilter}
            onChange={e => setClientFilter(e.target.value)}
            style={{ minWidth: 160 }}
          >
            <option value="all">All Projects</option>
            {clients.filter(c => c.isActive).map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>

          <span className="rpt-filter-label" style={{ marginLeft: 8 }}>Date range:</span>
          <input
            type="date"
            className="filter-input"
            style={{ width: 140 }}
            value={dateStart}
            onChange={e => setDateStart(e.target.value)}
          />
          <span style={{ color: 'var(--tx-faint)', fontSize: 12 }}>→</span>
          <input
            type="date"
            className="filter-input"
            style={{ width: 140 }}
            value={dateEnd}
            onChange={e => setDateEnd(e.target.value)}
          />

          <div style={{ display: 'flex', gap: 4 }}>
            {[['7d', 7], ['30d', 30], ['90d', 90]].map(([label, days]) => (
              <button
                key={label}
                className="btn-ghost"
                style={{ padding: '4px 9px', fontSize: 11 }}
                onClick={() => setPreset(days)}
              >
                {label}
              </button>
            ))}
            <button className="btn-ghost" style={{ padding: '4px 9px', fontSize: 11 }} onClick={setThisMonth}>
              This month
            </button>
            {(dateStart || dateEnd) && (
              <button className="btn-ghost" style={{ padding: '4px 9px', fontSize: 11 }} onClick={clearDates}>
                Clear ×
              </button>
            )}
          </div>
        </div>

        {/* ── Overview summary cards ── */}
        <div className="rpt-section">
          <div className="rpt-section-title">
            Overview{selectedClientName ? ` — ${selectedClientName}` : ''}{dateLabel ? ` · ${dateLabel}` : ''}
          </div>
          <div className="rpt-cards">
            <SummaryCard num={summary.totalScripts}    label="Scripts"          sub={`${summary.scriptsThisMonth} this month`} />
            <SummaryCard num={summary.approvedScripts} label="Approved Scripts" />
            <SummaryCard num={summary.liveVideos}      label="Live Videos"      />
            <SummaryCard num={summary.aiVideos}        label="AI Videos"        />
            <SummaryCard num={summary.livePosted}      label="Live Posted"      />
            <SummaryCard num={summary.aiPosted}        label="AI Posted"        />
          </div>
        </div>

        {/* ── 3 chart cards ── */}
        <div className="rpt-grid-3">
          <CollapsibleCard
            id="rpt-card-scripts"
            title="Scripts by Status"
            subtitle={`${summary.totalScripts} total`}
            onPrint={canPrint ? () => printSection('rpt-card-scripts', 'Scripts by Status') : null}
          >
            <BarChart statuses={QUAL_STATUSES} counts={qualCounts} colors={QUAL_COLORS} />
          </CollapsibleCard>

          <CollapsibleCard
            id="rpt-card-live"
            title="Live Production"
            subtitle={`${summary.liveVideos} total`}
            onPrint={canPrint ? () => printSection('rpt-card-live', 'Live Production by Status') : null}
          >
            <BarChart statuses={LIVE_STATUSES} counts={liveCounts} colors={LIVE_COLORS} />
          </CollapsibleCard>

          <CollapsibleCard
            id="rpt-card-ai"
            title="AI Production"
            subtitle={`${summary.aiVideos} total`}
            onPrint={canPrint ? () => printSection('rpt-card-ai', 'AI Production by Status') : null}
          >
            <BarChart statuses={AI_STATUSES} counts={aiCounts} colors={AI_COLORS} />
          </CollapsibleCard>
        </div>

        {/* ── Output by Client table ── */}
        <CollapsibleCard
          id="rpt-card-clients"
          title="Output by Project"
          subtitle={`${clientRows.length} active client${clientRows.length !== 1 ? 's' : ''}`}
          onPrint={canPrint ? () => printSection('rpt-card-clients', 'Output by Project') : null}
          onExport={canPrint ? exportClientRows : null}
        >
          <table className="rpt-table">
            <thead>
              <tr>
                <SortTh label="Project"      col="clientName"   sort={clientSort} setSort={setClientSort} />
                <SortTh label="Scripts"     col="scriptsCount" sort={clientSort} setSort={setClientSort} style={{ textAlign: 'right' }} />
                <SortTh label="Approved"    col="approvedCount"sort={clientSort} setSort={setClientSort} style={{ textAlign: 'right' }} />
                <SortTh label="Live Videos" col="liveVideos"   sort={clientSort} setSort={setClientSort} style={{ textAlign: 'right' }} />
                <SortTh label="AI Videos"   col="aiVideos"     sort={clientSort} setSort={setClientSort} style={{ textAlign: 'right' }} />
                <SortTh label="Live Posted" col="livePosted"   sort={clientSort} setSort={setClientSort} style={{ textAlign: 'right' }} />
                <SortTh label="AI Posted"   col="aiPosted"     sort={clientSort} setSort={setClientSort} style={{ textAlign: 'right' }} />
              </tr>
            </thead>
            <tbody>
              {clientRows.length === 0 && (
                <tr><td className="rpt-td" colSpan={7} style={{ color: 'var(--tx-faint)', textAlign: 'center' }}>No active projects</td></tr>
              )}
              {clientRows.map(({ clientId, clientName, initials, scriptsCount, approvedCount, liveVideos, aiVideos, livePosted, aiPosted }, idx) => (
                <tr key={clientId} style={idx === clientRows.length - 1 ? { borderBottom: 'none' } : {}}>
                  <td className="rpt-td">
                    <div className="rpt-client-cell">
                      <div className="rpt-initials">{initials}</div>
                      <span>{clientName}</span>
                    </div>
                  </td>
                  <td className="rpt-td" style={{ textAlign: 'right' }}>{scriptsCount}</td>
                  <td className="rpt-td" style={{ textAlign: 'right' }}>{approvedCount}</td>
                  <td className="rpt-td" style={{ textAlign: 'right' }}>{liveVideos}</td>
                  <td className="rpt-td" style={{ textAlign: 'right' }}>{aiVideos}</td>
                  <td className="rpt-td" style={{ textAlign: 'right' }}>{livePosted}</td>
                  <td className="rpt-td" style={{ textAlign: 'right' }}>{aiPosted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CollapsibleCard>

        {/* ── Scripts Written (Billing) — Admin / Manager / Power User ── */}
        {canSeeBilling && (
          <CollapsibleCard
            id="rpt-card-billing"
            title="Scripts Written"
            subtitle={`${billingRows.length} script${billingRows.length !== 1 ? 's' : ''}${dateLabel ? ` · ${dateLabel}` : ''}`}
            defaultOpen={false}
            onPrint={() => printSection('rpt-card-billing', 'Scripts Written')}
            onExport={exportBillingRows}
          >
            <table className="rpt-table">
              <thead>
                <tr>
                  <SortTh label="Writer"  col="writerName"  sort={billingSort} setSort={setBillingSort} />
                  <SortTh label="Project"  col="clientName"  sort={billingSort} setSort={setBillingSort} />
                  <SortTh label="Script"  col="scriptName"  sort={billingSort} setSort={setBillingSort} />
                  <SortTh label="Format"  col="format"      sort={billingSort} setSort={setBillingSort} />
                  <SortTh label="Status"  col="status"      sort={billingSort} setSort={setBillingSort} />
                  <SortTh label="Date"    col="dateEntered" sort={billingSort} setSort={setBillingSort} style={{ textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {billingRows.length === 0 && (
                  <tr><td className="rpt-td" colSpan={6} style={{ color: 'var(--tx-faint)', textAlign: 'center' }}>No scripts in range</td></tr>
                )}
                {billingRows.map((row, idx) => (
                  <tr key={idx} style={idx === billingRows.length - 1 ? { borderBottom: 'none' } : {}}>
                    <td className="rpt-td" style={{ fontWeight: 500 }}>{row.writerName}</td>
                    <td className="rpt-td">{row.clientName}</td>
                    <td className="rpt-td">{row.scriptName}</td>
                    <td className="rpt-td" style={{ fontSize: 11, color: 'var(--tx-muted)' }}>{row.format}</td>
                    <td className="rpt-td">
                      <span style={{ color: QUAL_COLORS[row.status] || 'var(--tx-muted)', fontWeight: 600, fontSize: 11 }}>
                        {row.status}
                      </span>
                    </td>
                    <td className="rpt-td" style={{ textAlign: 'right', color: 'var(--tx-muted)', fontSize: 11 }}>{row.dateEntered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollapsibleCard>
        )}

        {/* ── Scripts by Writer summary — Admin/Manager only ── */}
        {!isWriterOnly && (
          <CollapsibleCard
            id="rpt-card-writers"
            title="Scripts by Writer"
            subtitle={`${writerRows.length} writer${writerRows.length !== 1 ? 's' : ''}`}
            onPrint={canPrint ? () => printSection('rpt-card-writers', 'Scripts by Writer') : null}
            onExport={canPrint ? exportWriterRows : null}
          >
            <table className="rpt-table">
              <thead>
                <tr>
                  <SortTh label="Writer"          col="writerName"   sort={writerSort} setSort={setWriterSort} />
                  <SortTh label="Scripts Written"  col="total"        sort={writerSort} setSort={setWriterSort} style={{ textAlign: 'right' }} />
                  <SortTh label="Approved"         col="approved"     sort={writerSort} setSort={setWriterSort} style={{ textAlign: 'right' }} />
                  <SortTh label="Approval %"       col="approvalPct"  sort={writerSort} setSort={setWriterSort} style={{ textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {writerRows.length === 0 && (
                  <tr><td className="rpt-td" colSpan={4} style={{ color: 'var(--tx-faint)', textAlign: 'center' }}>No script data</td></tr>
                )}
                {writerRows.map((row, idx) => (
                  <tr key={row.writerName} style={idx === writerRows.length - 1 ? { borderBottom: 'none' } : {}}>
                    <td className="rpt-td">{row.writerName}</td>
                    <td className="rpt-td" style={{ textAlign: 'right' }}>{row.total}</td>
                    <td className="rpt-td" style={{ textAlign: 'right' }}>{row.approved}</td>
                    <td className="rpt-td" style={{ textAlign: 'right' }}>
                      <span style={{ color: row.approvalPct >= 75 ? 'var(--s-approved-t)' : row.approvalPct >= 40 ? 'var(--s-qual-t)' : 'var(--tx-muted)', fontFamily: 'var(--mono)' }}>
                        {row.approvalPct}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollapsibleCard>
        )}

        {/* ── Individual writer report — Admin/Manager selector ── */}
        {!isWriterOnly && (
          <CollapsibleCard
            id="rpt-card-writer-detail"
            title="Writer Report"
            subtitle="View a writer's full script list"
            defaultOpen={false}
            onExport={selectedWriterId ? exportWriterDetail : null}
            onPrint={selectedWriterId && canPrint ? () => printSection('rpt-card-writer-detail', `Writer Report — ${writerReportName}`) : null}
          >
            <div style={{ marginBottom: 16 }}>
              <select
                className="filter-input"
                value={selectedWriterId}
                onChange={e => setSelectedWriterId(e.target.value)}
                style={{ minWidth: 200 }}
              >
                <option value="">Select a writer…</option>
                {writerMembers.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>
            {selectedWriterId && (
              <>
                <div className="rpt-cards" style={{ marginBottom: 24 }}>
                  <SummaryCard num={writerStats.total}      label="Scripts Written" sub={writerReportName} />
                  <SummaryCard num={writerStats.approved}   label="Approved"        />
                  <SummaryCard num={writerStats.inQual}     label="In Qual"         />
                  <SummaryCard num={writerStats.backburner} label="Backburner"      />
                </div>
                <WriterScriptTable scripts={writerReportScripts} clients={clients} />
              </>
            )}
          </CollapsibleCard>
        )}

        {/* ── Writer's personal report — Writer role only ── */}
        {isWriterOnly && (
          <CollapsibleCard
            id="rpt-card-my-scripts"
            title="My Scripts"
            subtitle={`${writerStats.total} total`}
            onPrint={() => printSection('rpt-card-my-scripts', 'My Scripts')}
            onExport={() => downloadCSV(writerReportScripts.map(s => ({
              Script: s.scriptName,
              Client: s.client?.name || '—',
              Status: s.qualStatus,
              Date:   s.dateEntered ? s.dateEntered.slice(0, 10) : '—',
            })), 'my-scripts.csv')}
          >
            <div className="rpt-cards" style={{ marginBottom: 24 }}>
              <SummaryCard num={writerStats.total}      label="Scripts Written" />
              <SummaryCard num={writerStats.approved}   label="Approved"        />
              <SummaryCard num={writerStats.inQual}     label="In Qual"         />
              <SummaryCard num={writerStats.backburner} label="Backburner"      />
            </div>
            <WriterScriptTable scripts={writerReportScripts} clients={clients} />
          </CollapsibleCard>
        )}

        {/* ── Editor Performance — Admin/Manager only ── */}
        {!isWriterOnly && (
          <CollapsibleCard
            id="rpt-card-editors"
            title="Editor Performance"
            subtitle={`${sortedEditorStats.length} editor${sortedEditorStats.length !== 1 ? 's' : ''}`}
            defaultOpen={false}
            onPrint={canPrint ? () => printSection('rpt-card-editors', 'Editor Performance') : null}
            onExport={canPrint ? exportEditorStats : null}
          >
            <table className="rpt-table">
              <thead>
                <tr>
                  <SortTh label="Editor"              col="name"               sort={editorSort} setSort={setEditorSort} />
                  <SortTh label="Avg Turnaround (days)" col="avgTurnaroundDays" sort={editorSort} setSort={setEditorSort} style={{ textAlign: 'right' }} />
                  <SortTh label="Revision Rate"        col="revisionRate"       sort={editorSort} setSort={setEditorSort} style={{ textAlign: 'right' }} />
                  <SortTh label="Queue Depth"          col="queueDepth"         sort={editorSort} setSort={setEditorSort} style={{ textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {sortedEditorStats.length === 0 && (
                  <tr><td className="rpt-td" colSpan={4} style={{ color: 'var(--tx-faint)', textAlign: 'center' }}>No editor data</td></tr>
                )}
                {sortedEditorStats.map((row, idx) => (
                  <tr key={row.id} style={idx === sortedEditorStats.length - 1 ? { borderBottom: 'none' } : {}}>
                    <td className="rpt-td" style={{ fontWeight: 500 }}>{row.name}</td>
                    <td className="rpt-td" style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>
                      {row.avgTurnaroundDays > 0 ? row.avgTurnaroundDays.toFixed(1) : '—'}
                    </td>
                    <td className="rpt-td" style={{ textAlign: 'right' }}>
                      <span style={{ color: row.revisionRate > 30 ? 'var(--s-incomplete-t)' : row.revisionRate > 15 ? 'var(--s-qual-t)' : 'var(--s-approved-t)', fontFamily: 'var(--mono)' }}>
                        {row.totalQuald > 0 ? `${row.revisionRate}%` : '—'}
                      </span>
                    </td>
                    <td className="rpt-td" style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>
                      <span style={{ color: row.queueDepth > 5 ? 'var(--s-incomplete-t)' : 'var(--tx-secondary)' }}>
                        {row.queueDepth}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CollapsibleCard>
        )}

      </div>
    </div>
  );
}

// ── Writer script list table ───────────────────────────────────────────────────

function WriterScriptTable({ scripts: list, clients }) {
  const [sort, setSort] = useState({ col: 'dateEntered', dir: 'desc' });

  const sorted = useMemo(() => {
    if (!sort.col) return list;
    return [...list].sort((a, b) => {
      let av = '', bv = '';
      if      (sort.col === 'scriptName')  { av = a.scriptName || ''; bv = b.scriptName || ''; }
      else if (sort.col === 'clientName')  { av = a.client?.name || ''; bv = b.client?.name || ''; }
      else if (sort.col === 'qualStatus')  { av = a.qualStatus || ''; bv = b.qualStatus || ''; }
      else if (sort.col === 'dateEntered') { av = a.dateEntered || ''; bv = b.dateEntered || ''; }
      const cmp = av.localeCompare(bv);
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [list, sort]);

  if (list.length === 0) {
    return (
      <table className="rpt-table">
        <tbody>
          <tr><td className="rpt-td" colSpan={4} style={{ color: 'var(--tx-faint)', textAlign: 'center' }}>No scripts found</td></tr>
        </tbody>
      </table>
    );
  }

  return (
    <table className="rpt-table">
      <thead>
        <tr>
          <SortTh label="Script Name" col="scriptName"  sort={sort} setSort={setSort} />
          <SortTh label="Project"      col="clientName"  sort={sort} setSort={setSort} />
          <SortTh label="Status"      col="qualStatus"  sort={sort} setSort={setSort} />
          <SortTh label="Date"        col="dateEntered" sort={sort} setSort={setSort} style={{ textAlign: 'right' }} />
        </tr>
      </thead>
      <tbody>
        {sorted.map((s, idx) => (
          <tr key={s.id} style={idx === sorted.length - 1 ? { borderBottom: 'none' } : {}}>
            <td className="rpt-td">{s.scriptName}</td>
            <td className="rpt-td">{s.client?.name || clients.find(c => c.id === s.clientId)?.name || '—'}</td>
            <td className="rpt-td">
              <span style={{ color: QUAL_COLORS[s.qualStatus] || 'var(--tx-muted)', fontWeight: 600, fontSize: 11 }}>
                {s.qualStatus}
              </span>
            </td>
            <td className="rpt-td" style={{ textAlign: 'right', color: 'var(--tx-muted)', fontSize: 11 }}>
              {s.dateEntered
                ? new Date(s.dateEntered).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
